import React, { useState } from 'react';
import {
  SiteImposedCharge,
  SiteImposedChargeCreate,
  SiteImposedChargeUpdate,
  FeeFrequency,
  FeeTrigger,
  FEE_FREQUENCY_LABELS
} from '../../../types/site';
import { StoreCharge } from '../../../services/configService';
import { siteService } from '../../../services/siteService';
import { formatCurrency } from '../../../utils/formatters';
import EffectiveSummary, { EffectiveItem, computeAdditiveEffectiveItems } from './EffectiveSummary';

// ============================================================================
// SiteFeesFormV2 - Dedicated form for fees (service fees)
// UI Label: "Fees" | DB Tables: store_imposed_charges, site_imposed_charges
// Hierarchy: ADDITIVE - Both store and site fees apply together
// ============================================================================

interface SiteFeesFormV2Props {
  siteId: number;
  imposedCharges: SiteImposedCharge[];
  storeCharges: StoreCharge[];
  onChange: (charges: SiteImposedCharge[]) => void;
}

const FEE_TRIGGER_LABELS: Record<FeeTrigger, string> = {
  any: 'Any Change',
  site_changed: 'Site Changed',
  date_changed: 'Date Changed',
  occupancy_changed: 'Occupancy Changed',
  site_rebooking: 'Site Rebooking'
};

interface FeeFormData {
  item: string;
  amount: number;
  flat: boolean;
  fee_trigger: FeeTrigger;
  frequency: FeeFrequency;
  active: boolean;
}

const emptyFormData: FeeFormData = {
  item: '',
  amount: 0,
  flat: true,
  fee_trigger: 'any',
  frequency: 'once',
  active: true
};

const SiteFeesFormV2: React.FC<SiteFeesFormV2Props> = ({
  siteId,
  imposedCharges,
  storeCharges,
  onChange
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<FeeFormData>(emptyFormData);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Compute effective fees (ADDITIVE model - both store and site apply)
  const effectiveFees = computeAdditiveEffectiveItems<StoreCharge, SiteImposedCharge>(
    storeCharges.filter(c => c.active),
    imposedCharges.filter(c => c.active)
  );

  const handleAddNew = () => {
    setFormData(emptyFormData);
    setEditingId(null);
    setShowAddForm(true);
    setError(null);
  };

  const handleEdit = (charge: SiteImposedCharge) => {
    setFormData({
      item: charge.item,
      amount: charge.amount,
      flat: charge.flat,
      fee_trigger: charge.fee_trigger,
      frequency: charge.frequency,
      active: charge.active
    });
    setEditingId(charge.id);
    setShowAddForm(true);
    setError(null);
  };

  const handleCancel = () => {
    setFormData(emptyFormData);
    setEditingId(null);
    setShowAddForm(false);
    setError(null);
  };

  const handleSave = async () => {
    if (!formData.item.trim()) {
      setError('Fee name is required');
      return;
    }

    if (formData.amount <= 0) {
      setError('Amount must be greater than 0');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (editingId) {
        // Update existing
        const updateData: SiteImposedChargeUpdate = {
          item: formData.item.trim(),
          amount: formData.amount,
          flat: formData.flat,
          fee_trigger: formData.fee_trigger,
          frequency: formData.frequency,
          active: formData.active
        };
        const updated = await siteService.updateSiteCharge(editingId, updateData);
        onChange(imposedCharges.map(c => c.id === editingId ? updated : c));
      } else {
        // Create new
        const createData: SiteImposedChargeCreate = {
          site_id: siteId,
          item: formData.item.trim(),
          amount: formData.amount,
          flat: formData.flat,
          fee_trigger: formData.fee_trigger,
          frequency: formData.frequency,
          active: formData.active
        };
        const created = await siteService.createSiteCharge(createData);
        onChange([...imposedCharges, created]);
      }

      handleCancel();
    } catch (err) {
      setError('Failed to save fee');
      console.error('Error saving fee:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (chargeId: number) => {
    if (!window.confirm('Delete this site-specific fee?')) {
      return;
    }

    try {
      await siteService.deleteSiteCharge(chargeId);
      onChange(imposedCharges.filter(c => c.id !== chargeId));
    } catch (err) {
      console.error('Error deleting fee:', err);
    }
  };

  // Render fee info
  const renderFeeInfo = (fee: StoreCharge | SiteImposedCharge) => {
    const amountStr = fee.flat ? formatCurrency(fee.amount) : `${fee.amount}%`;
    const frequencyLabel = FEE_FREQUENCY_LABELS[fee.frequency as FeeFrequency] || fee.frequency;
    const triggerLabel = fee.fee_trigger ? FEE_TRIGGER_LABELS[fee.fee_trigger as FeeTrigger] || fee.fee_trigger : '';

    return (
      <>
        <span className="fee-name">{fee.item}</span>
        <span className="fee-details">
          {amountStr} {frequencyLabel}
          {triggerLabel && ` | Trigger: ${triggerLabel}`}
        </span>
      </>
    );
  };

  return (
    <div className="site-fees-form-v2">
      {/* Error Display */}
      {error && (
        <div className="form-error-banner">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Store Default Fees Section */}
      <div className="hierarchy-section store-defaults-section">
        <div className="hierarchy-section-header">
          <h4 className="hierarchy-section-title">Store Default Fees</h4>
          <span className="source-badge store">Store Default</span>
        </div>
        <p className="hierarchy-section-description">
          These fees are configured at the store level and <strong>always apply</strong> to all sites.
        </p>

        {storeCharges.filter(c => c.active).length === 0 ? (
          <p className="hierarchy-empty-message">
            No store-level fees configured.
          </p>
        ) : (
          <div className="hierarchy-items-list">
            {storeCharges
              .filter(c => c.active)
              .map(charge => (
                <div key={charge.id} className="hierarchy-item store-default-item">
                  <div className="hierarchy-item-content">
                    {renderFeeInfo(charge)}
                  </div>
                  <span className="always-applies-badge">Always Applies</span>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Site-Specific Fees Section */}
      <div className="hierarchy-section site-overrides-section">
        <div className="hierarchy-section-header">
          <h4 className="hierarchy-section-title">Site-Specific Fees</h4>
          <span className="source-badge site">Site Specific</span>
        </div>
        <p className="hierarchy-section-description">
          Additional fees specific to this site (e.g., waterfront premium, lakeside surcharge).
          These are <strong>added to</strong> store fees, not replacing them.
        </p>

        {imposedCharges.length === 0 ? (
          <p className="hierarchy-empty-message">
            No site-specific fees configured. Only store fees will apply.
          </p>
        ) : (
          <div className="hierarchy-items-list">
            {imposedCharges.map(charge => (
              <div
                key={charge.id}
                className={`hierarchy-item site-override-item ${!charge.active ? 'inactive' : ''}`}
              >
                <div className="hierarchy-item-content">
                  {renderFeeInfo(charge)}
                  {!charge.active && <span className="inactive-badge">Inactive</span>}
                </div>
                <div className="hierarchy-item-actions">
                  <button
                    type="button"
                    className="btn btn-text btn-sm"
                    onClick={() => handleEdit(charge)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-text btn-sm btn-danger"
                    onClick={() => handleDelete(charge.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="fee-form-card">
            <h4>{editingId ? 'Edit Site Fee' : 'Add Site-Specific Fee'}</h4>

            <div className="form-row">
              <div className="form-group form-group-wide">
                <label>Fee Name <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.item}
                  onChange={(e) => setFormData({ ...formData, item: e.target.value })}
                  placeholder="e.g., Waterfront Premium, Lakeside Surcharge"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Amount <span className="required">*</span></label>
                <input
                  type="number"
                  value={formData.amount || ''}
                  onChange={(e) => setFormData({ ...formData, amount: Number(e.target.value) })}
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="form-group">
                <label>Amount Type</label>
                <select
                  value={formData.flat ? 'flat' : 'percent'}
                  onChange={(e) => setFormData({ ...formData, flat: e.target.value === 'flat' })}
                >
                  <option value="flat">Flat Amount ($)</option>
                  <option value="percent">Percentage (%)</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Fee Trigger</label>
                <select
                  value={formData.fee_trigger}
                  onChange={(e) => setFormData({ ...formData, fee_trigger: e.target.value as FeeTrigger })}
                >
                  {Object.entries(FEE_TRIGGER_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
                <span className="form-hint">When this fee applies</span>
              </div>
              <div className="form-group">
                <label>Frequency</label>
                <select
                  value={formData.frequency}
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value as FeeFrequency })}
                >
                  {Object.entries(FEE_FREQUENCY_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="toggle-row">
              <div className="toggle-label">
                <span>Active</span>
                <span>Enable this fee</span>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            <div className="form-actions">
              <button className="btn btn-secondary" onClick={handleCancel} disabled={saving}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : editingId ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        )}

        {!showAddForm && (
          <button
            type="button"
            className="btn btn-primary btn-sm hierarchy-add-btn"
            onClick={handleAddNew}
          >
            + Add Site-Specific Fee
          </button>
        )}
      </div>

      {/* Effective Fees Summary */}
      <EffectiveSummary
        title="Effective Fees for This Site"
        description="All fees that will apply to reservations at this site (store fees + site fees)."
        items={effectiveFees as EffectiveItem<StoreCharge | SiteImposedCharge>[]}
        renderItem={(item, source) => (
          <div className="effective-fee-item">
            {renderFeeInfo(item)}
          </div>
        )}
        emptyMessage="No fees configured."
      />
    </div>
  );
};

export default SiteFeesFormV2;
